import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
    BadgeCheck,
    Banknote,
    Check,
    Circle,
    CreditCard,
    FileSignature,
    Link2,
    MapPin,
    RefreshCw,
    ShieldCheck,
    X,
    ListChecks,
} from "lucide-react";

interface Props {
    order: any;
    activeTab: string;
}

type StepStatus = "COMPLETED" | "CURRENT" | "PENDING" | "FAILED";
type Tone = "info" | "success" | "danger";

interface JourneyStep {
    key: string;
    label: string;
    status: StepStatus;
    detail: string | null;
}

interface JourneyData {
    journeyId: string;
    lender?: string;
    phase: string;
    journeyStatus: string;
    currentStatus: { label: string; detail: string | null; tone: Tone };
    steps: JourneyStep[];
    totalSteps: number;
    completedCount: number;
    updatedAt?: string;
}

// Icon per step key. Unknown keys fall back to a plain circle.
const STEP_ICONS: Record<string, React.ReactNode> = {
    LINK_GENERATED: <Link2 size={14} />,
    ADDRESS_SUBMITTED: <MapPin size={14} />,
    PRE_APPROVED: <BadgeCheck size={14} />,
    KYC: <ShieldCheck size={14} />,
    MANDATE: <CreditCard size={14} />,
    AGREEMENT: <FileSignature size={14} />,
    DOWNPAYMENT: <Banknote size={14} />,
    SCHEME_SELECTED: <ListChecks size={14} />,
};

const circleClass: Record<StepStatus, string> = {
    COMPLETED: "bg-green-500 text-white shadow-sm",
    CURRENT: "bg-blue-50 text-blue-600 ring-2 ring-blue-500",
    FAILED: "bg-red-500 text-white shadow-sm",
    PENDING: "bg-gray-100 text-gray-500",
};

const labelClass: Record<StepStatus, string> = {
    COMPLETED: "text-green-600 font-semibold",
    CURRENT: "text-blue-600 font-semibold",
    FAILED: "text-red-600 font-semibold",
    PENDING: "text-gray-600",
};

const toneClass: Record<Tone, string> = {
    info: "bg-blue-50 text-blue-700 border-blue-100",
    success: "bg-green-50 text-green-700 border-green-100",
    danger: "bg-red-50 text-red-700 border-red-100",
};

const CustomerStatusTracker = ({ order, activeTab }: Props) => {
    const [journey, setJourney] = useState<JourneyData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchJourneyStatus = useCallback(async () => {
        if (!order?.orderId) return;

        try {
            setLoading(true);
            setError(null);

            const res = await axios.get(
                `${import.meta.env.VITE_API_BASE_URL}/adminorder/v1/customerStatus`,
                {
                    params: { orderId: order.orderId },
                    headers: { token: localStorage.getItem("token") },
                }
            );

            setJourney(res?.data?.data || null);
        } catch (err) {
            console.error("Status API error", err);
            setError("Unable to load journey status. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [order?.orderId]);

    useEffect(() => {
        if (activeTab !== "status") return;
        if (order?.status === "Expired") return;
        fetchJourneyStatus();
    }, [activeTab, order?.status, fetchJourneyStatus]);

    // 🚫 Expired order
    if (order?.status === "Expired") {
        return (
            <div className="bg-white p-4 rounded-xl shadow-sm text-sm text-red-500">
                Order is expired. Lender journey is not available.
            </div>
        );
    }

    const steps = journey?.steps || [];

    const renderStepIcon = (step: JourneyStep) => {
        if (step.status === "COMPLETED") return <Check size={14} strokeWidth={3} />;
        if (step.status === "FAILED") return <X size={14} strokeWidth={3} />;
        return STEP_ICONS[step.key] || <Circle size={14} />;
    };

    return (
        <div className="w-full px-4 py-4 bg-white rounded-xl shadow-sm border border-gray-100">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800">
                    Customer Application Status
                    {/* {journey?.lender && (
                        <span className="ml-2 text-xs font-normal text-gray-500">
                            ({journey.lender})
                        </span>
                    )} */}
                </h3>

                <div className="flex items-center gap-3">
                    {journey && (
                        <span className="text-xs text-gray-500">
                            {journey.completedCount}/{journey.totalSteps} Completed
                        </span>
                    )}
                    <button
                        onClick={fetchJourneyStatus}
                        disabled={loading}
                        title="Refresh"
                        className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-50"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                    </button>
                </div>
            </div>

            {/* States */}
            {error && <p className="text-sm text-red-500">{error}</p>}

            {!error && !loading && !journey && (
                <p className="text-sm text-gray-500">Journey not started yet.</p>
            )}

            {!error && loading && !journey && (
                <p className="text-sm text-gray-500">Loading journey status...</p>
            )}

            {journey && !error && (
                <>
                    {/* Current status banner */}
                    <div
                        className={`mb-5 px-3 py-2 rounded-lg border text-sm ${toneClass[journey.currentStatus.tone]}`}
                    >
                        <span className="font-semibold">{journey.currentStatus.label}</span>
                        {journey.currentStatus.detail && (
                            <span className="ml-2 text-xs opacity-80">
                                {journey.currentStatus.detail}
                            </span>
                        )}
                    </div>

                    {/* Stepper */}
                    <div className="w-full overflow-x-auto">
                        <div
                            className="grid items-start relative min-w-max sm:min-w-0"
                            style={{
                                gridTemplateColumns: `repeat(${steps.length}, minmax(110px, 1fr))`,
                            }}
                        >
                            {steps.map((step, index) => (
                                <div
                                    key={step.key}
                                    className="flex flex-col items-center relative px-2"
                                >
                                    {/* Connection line to next step */}
                                    {index !== steps.length - 1 && (
                                        <div
                                            className={`absolute top-4 left-1/2 w-full h-[2px] transition-colors duration-300 ${
                                                step.status === "COMPLETED"
                                                    ? "bg-green-500"
                                                    : "bg-gray-200"
                                            }`}
                                        />
                                    )}

                                    {/* Circle */}
                                    <div
                                        className={`w-8 h-8 flex items-center justify-center rounded-full z-10 transition-all duration-200 ${circleClass[step.status]}`}
                                    >
                                        {renderStepIcon(step)}
                                    </div>

                                    {/* Label */}
                                    <p
                                        className={`mt-2 text-xs text-center transition-colors duration-200 ${labelClass[step.status]}`}
                                    >
                                        {step.label}
                                    </p>

                                    {/* Detail (amount, sub-step, txn info...) */}
                                    {step.detail && (
                                        <p className="mt-0.5 text-[11px] text-center text-gray-500 leading-tight">
                                            {step.detail}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CustomerStatusTracker;